/**
 * DIY 故事编辑器状态管理 - Zustand Store
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface EditorPage {
  pageNumber: number;
  content: string;
  illustrationPrompt: string;
  charactersInPage?: string[];
}

export interface EditorCharacter {
  id: string;
  name: string;
  description: string;
  type: 'protagonist' | 'supporting';
  species?: string;
  photoUrl?: string;
  characterType?: 'human' | 'animal';
}

interface DIYEditorState {
  storyId: string | null;
  storyTitle: string;
  pages: EditorPage[];
  cover: {
    title: string;
    illustrationPrompt: string;
    author: string;
  };
  characters: EditorCharacter[];
  currentPageIndex: number; // -1 表示封面，0~N-1 表示第1~N页
  hasUnsavedChanges: boolean;
  isLoading: boolean;
  isSaving: boolean;
  saveError: string | null;

  // Actions
  setStoryId: (id: string) => void;
  initFromStory: (storyData: any, textContent: any, supportingChars?: any[]) => void;
  updatePage: (index: number, data: Partial<EditorPage>) => void;
  updateCover: (data: Partial<DIYEditorState['cover']>) => void;
  setCurrentPage: (index: number) => void;
  markSaved: () => void;
  markUnsaved: () => void;
  setLoading: (loading: boolean) => void;
  setSaving: (saving: boolean) => void;
  setSaveError: (error: string | null) => void;
  getTextContent: () => any;
  validateAllPages: () => { valid: boolean; missing: number[] };
  reset: () => void;
}

const initialState = {
  storyId: null,
  storyTitle: '',
  pages: [],
  cover: { title: '', illustrationPrompt: '', author: '' },
  characters: [],
  currentPageIndex: -1,
  hasUnsavedChanges: false,
  isLoading: true,
  isSaving: false,
  saveError: null,
};

export const useDIYEditorStore = create<DIYEditorState>()(
  persist(
    (set, get) => ({
      ...initialState,

      setStoryId: (id) => set({ storyId: id }),

      initFromStory: (storyData, textContent, supportingChars = []) => {
        const pageCount = storyData.pageCount || 8;
        const existingPages = textContent?.pages || [];

        const pages: EditorPage[] = [];
        for (let i = 1; i <= pageCount; i++) {
          const existing = existingPages.find((p: any) => p.pageNumber === i);
          pages.push({
            pageNumber: i,
            content: existing?.text || existing?.content || '',
            illustrationPrompt: existing?.illustrationPrompt || '',
          });
        }

        // 封面数据：优先从 textContent.cover 读取，其次从 story 读取
        const existingCover = textContent?.cover;
        const coverTitle = existingCover?.title || storyData.title || '';
        const coverPrompt = existingCover?.illustrationPrompt || '';
        const coverAuthor = existingCover?.author || '';

        // 角色数据
        const charData = storyData.characterData || {};
        const config = typeof storyData.config === 'string' ? JSON.parse(storyData.config) : (storyData.config || {});
        const characters: EditorCharacter[] = [];
        if (charData.name || config.characterName) {
          characters.push({
            id: 'protagonist',
            name: charData.name || config.characterName || '主角',
            description: charData.description || config.appearance || '',
            type: 'protagonist',
            species: charData.species || config.species,
            photoUrl: storyData.photoUrl,
          });
        }
        // 配角
        for (const sc of supportingChars) {
          characters.push({
            id: sc.id || `sc-${Date.now()}`,
            name: sc.name,
            description: sc.appearance || '',
            type: 'supporting',
            species: sc.species || sc.type,
            characterType: sc.characterType || 'animal',
          });
        }

        set({
          storyTitle: storyData.title || '',
          pages,
          cover: {
            title: coverTitle,
            illustrationPrompt: coverPrompt,
            author: coverAuthor,
          },
          characters,
          currentPageIndex: -1, // 默认选中封面
          hasUnsavedChanges: false,
          isLoading: false,
          saveError: null,
        });
      },

      updatePage: (index, data) =>
        set((state) => {
          const newPages = [...state.pages];
          newPages[index] = { ...newPages[index], ...data };
          return { pages: newPages, hasUnsavedChanges: true };
        }),

      updateCover: (data) =>
        set((state) => ({
          cover: { ...state.cover, ...data },
          hasUnsavedChanges: true,
        })),

      setCurrentPage: (index) => set({ currentPageIndex: index }),

      markSaved: () => set({ hasUnsavedChanges: false }),
      markUnsaved: () => set({ hasUnsavedChanges: true }),
      setLoading: (loading) => set({ isLoading: loading }),
      setSaving: (saving) => set({ isSaving: saving }),
      setSaveError: (error) => set({ saveError: error }),

      getTextContent: () => {
        const state = get();
        return {
          cover: state.cover,
          pages: state.pages.map((p) => ({
            pageNumber: p.pageNumber,
            text: p.content,
            content: p.content,
            illustrationPrompt: p.illustrationPrompt,
            charactersInPage: p.charactersInPage,
          })),
        };
      },

      validateAllPages: () => {
        const { pages, cover } = get();
        const missing: number[] = [];

        if (!cover.illustrationPrompt.trim()) {
          missing.push(0); // 0 表示封面
        }

        pages.forEach((page) => {
          if (!page.content.trim() || !page.illustrationPrompt.trim()) {
            missing.push(page.pageNumber);
          }
        });

        return { valid: missing.length === 0, missing };
      },

      reset: () => set(initialState),
    }),
    {
      name: 'diy-editor-store',
      skipHydration: true,
    }
  )
);
